import type { RootOptions } from 'react-dom/client';

/**
 * Root-level error reporting (issue #376).
 *
 * WHY THIS EXISTS AT ALL. `AppErrorBoundary` controls what the DOM shows, but
 * not what React reports: with no override, React's default `onCaughtError` /
 * `onUncaughtError` writes the whole thrown `Error` to the console, message
 * included. A render that threw `new Error(case.description)` would then put
 * case text into browser logs and anything that ships them — the same
 * disclosure the fallback DOM refuses, arriving by a second route.
 *
 * So the root is given handlers that report ONLY the component stack. That
 * stack names component TYPES (`at CaseDetailPanel`), never the rendered value,
 * and is enough to locate a fault in a built app.
 *
 * `onRecoverableError` is overridden for the same reason, not because this app
 * expects recoverable errors: it exists so no future one can bypass the rule by
 * choosing a different React channel. The thrown value is unused in every
 * handler, deliberately — see the parameter names.
 */
export function reportRenderFailure(info: { readonly componentStack?: string | undefined }): void {
  console.error('[rp] render failed', info.componentStack ?? '');
}

export const rootErrorOptions: RootOptions = {
  onCaughtError: (_error, info) => {
    reportRenderFailure(info);
  },
  onUncaughtError: (_error, info) => {
    reportRenderFailure(info);
  },
  onRecoverableError: (_error, info) => {
    reportRenderFailure(info);
  },
};
